import { Box, Flex, Stack, Tooltip, useToken } from '@chakra-ui/react';
import React, { forwardRef, Ref, RefAttributes, useCallback } from 'react';
import Select, {
  components as Components,
  GroupBase,
  InputProps,
  OptionsOrGroups,
  Props,
  SelectInstance,
} from 'react-select';

import { IoAlertCircleIcon } from '@/components/common/Icons/IoAlertCircleIcon';
import { WizardAskButton } from '@/components/wizard/WizardAskButton';
import { useNextTranslation } from '@/i18n/client';

import { DropdownOption } from '../DropdownInput/typings';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Label from '../Label/Label';
import ClearIndicator from './components/ClearIndicator';
import DropdownIndicator from './components/DropdownIndicator';
import MultiValueRemove from './components/MultiValueRemove';
import OptionWrapper from './components/OptionWrapper';
import SearchDropdownIndicator from './components/SearchDropdownIndicator';
import VirtuosoMenuList from './components/VirtuosoMenuList';
import {
  getControlStyles,
  getDropdownIndicator,
  getMultiValueLabel,
  getMultiValueRemove,
  getMultiValueStyles,
  getOptionStyles,
  getPlaceholderStyles,
  getValueContainerStyles,
} from './utils';

export type SearchDropdownInputProps<
  Option = DropdownOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & {
  testId?: string;
  label?: string;
  tooltipLabel?: string;
  secondaryLabel?: string;
  errorMessage?: string;
  displayName?: string;
  width?: string | number;
  errorMessageType?: 'underMessage' | 'tooltip';
  valueContainerStyle?: React.CSSProperties;
  maxDisplayOptions?: number;
  enableWizardAsk?: boolean;
  wizardAskPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  required?: boolean;
  description?: React.ReactNode;
  isInsertEmptyOption?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
};

export type SearchDropdownInput = <
  Option = DropdownOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SearchDropdownInputProps<Option, IsMulti, Group> & RefAttributes<SelectInstance<Option, IsMulti, Group>>,
) => React.ReactNode;

const SearchDropdownInput = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  {
    testId,
    label,
    tooltipLabel,
    secondaryLabel,
    errorMessage,
    errorMessageType = 'underMessage',
    width,
    isSearchable = true,
    isClearable = true,
    isMulti = false as IsMulti,
    isInsertEmptyOption = false,
    options = [],
    styles = {},
    components = {},
    valueContainerStyle = {},
    maxDisplayOptions = 6,
    enableWizardAsk = false,
    wizardAskPlacement = 'bottom-start',
    required = false,
    menuPlacement = 'auto',
    ...props
  }: SearchDropdownInputProps<Option, IsMulti, Group>,
  ref: Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  const { t } = useNextTranslation();

  const [gray200, blue50, blue500, red500, gray400, blue100, blue800] = useToken('colors', [
    'gray.200',
    'blue.50',
    'blue.500',
    'red.500',
    'gray400',
    'blue.100',
    'blue.800',
  ]);

  const getInputComponent = useCallback(
    (props: InputProps<Option, IsMulti, Group> & { testId?: string }) => {
      return <Components.Input {...props} data-testid={testId} />;
    },
    [testId],
  );

  const labelComponent = enableWizardAsk ? (
    <WizardAskButton question={`${label || ''}`} testid={testId} placement={wizardAskPlacement}>
      <Label
        name={props.name}
        label={label}
        secondaryLabel={secondaryLabel}
        showTooltipIcon={Boolean(tooltipLabel)}
        tooltipLabel={tooltipLabel}
        required={required}
      />
    </WizardAskButton>
  ) : (
    <Label
      name={props.name}
      label={label}
      secondaryLabel={secondaryLabel}
      showTooltipIcon={Boolean(tooltipLabel)}
      tooltipLabel={tooltipLabel}
      required={required}
    />
  );

  return (
    <Stack gap={1} position='relative' width={width || 'auto'} justify={'stretch'}>
      {labelComponent}
      <Flex gap={1} align={'center'}>
        <Box flex={1} width={'100%'}>
          <Select
            menuPlacement={menuPlacement}
            ref={ref}
            isSearchable={isSearchable}
            isClearable={isClearable}
            isMulti={isMulti}
            menuPortalTarget={document.body}
            placeholder={props.placeholder ?? isSearchable ? t('common.enterKeyword') : t('common.pleaseSelect')}
            styles={{
              control: (base, props) => getControlStyles(base, props, { gray200, blue500, red500 }, !!errorMessage),
              valueContainer: (base) => ({ ...getValueContainerStyles(base), ...valueContainerStyle }),
              placeholder: getPlaceholderStyles,
              option: (base, props) => getOptionStyles(base, props, { blue50, blue500 }),
              multiValue: (base, props) => getMultiValueStyles(base, props, { blue50, blue100, blue800 }),
              multiValueLabel: (base) => getMultiValueLabel(base, { blue800 }),
              multiValueRemove: (base) => getMultiValueRemove(base, { blue100, gray400, blue800 }),
              dropdownIndicator: getDropdownIndicator,
              menu: (base) => ({ ...base, marginTop: 0, border: `1px solid ${gray200}`, boxShadow: 'none' }),
              input: (base) => ({ ...base, padding: 0, margin: 0 }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              ...styles,
            }}
            components={{
              ClearIndicator: isSearchable ? ClearIndicator : undefined,
              DropdownIndicator: (props) =>
                isSearchable ? (
                  <SearchDropdownIndicator {...props} testId={testId ? `${testId}-indicator` : undefined} />
                ) : (
                  // for click single select
                  <DropdownIndicator {...props} testId={testId ? `${testId}-indicator` : undefined} />
                ),
              MultiValueRemove,
              IndicatorSeparator: null,
              MenuList: (props) => <VirtuosoMenuList {...props} maxDisplayOptions={maxDisplayOptions} />,
              Option: OptionWrapper,
              Input: getInputComponent,
              ...components,
            }}
            options={
              isInsertEmptyOption
                ? ([{ label: t('common.pleaseSelect'), value: '' }, ...options] as OptionsOrGroups<Option, Group>)
                : options
            }
            {...props}
          />
        </Box>
        {errorMessage && errorMessageType === 'tooltip' && (
          <Tooltip label={errorMessage} hasArrow bgColor='gray.900'>
            <IoAlertCircleIcon color='red.500' />
          </Tooltip>
        )}
      </Flex>
      {errorMessage && errorMessageType === 'underMessage' ? (
        <ErrorMessage message={errorMessage} data-testid={`error-message-${props.name}`} />
      ) : (
        props.description
      )}
    </Stack>
  );
};

export default forwardRef(SearchDropdownInput) as SearchDropdownInput;
